import React, { useCallback, useEffect, useRef, useState } from "react";
import "./Products.css";
import axiosConfig from "../../Service/axiosConfig";
import Product from "./Product";

const DEFAULT_SORT = "Relevance";

const normalizeProducts = (items = []) =>
  [...items].sort((a, b) => (a.sort ?? 9999) - (b.sort ?? 9999));

const sortProducts = (items, sortBy) => {
  const normalizedItems = [...items];

  switch (sortBy) {
    case "Price (Low to High)":
      return normalizedItems.sort((a, b) => a.price - b.price);
    case "Price (High to Low)":
      return normalizedItems.sort((a, b) => b.price - a.price);
    case "Discount (High to Low)":
      return normalizedItems.sort((a, b) => {
        const getDiscount = (item) => {
          let discount = 0;
          if (item.discount_percentage) {
            discount = parseFloat(item.discount_percentage);
          } else if (parseFloat(item.price) > 0 && parseFloat(item.offer_price) > 0 && parseFloat(item.price) > parseFloat(item.offer_price)) {
            discount = ((parseFloat(item.price) - parseFloat(item.offer_price)) / parseFloat(item.price)) * 100;
          }
          console.log(`Item: ${item.name}, Price: ${item.price}, Offer: ${item.offer_price}, Discount%: ${item.discount_percentage}, Calculated Discount: ${discount}`);
          return discount;
        };
        return getDiscount(b) - getDiscount(a);
      });
    case "Name (A to Z)":
      return normalizedItems.sort((a, b) => a.name.localeCompare(b.name));
    default:
      return normalizeProducts(normalizedItems);
  }
};

const normalizeNextUrl = (url) =>
  url?.startsWith("http:") ? url.replace(/^http:/, "https:") : url;

const ProductsList = ({ subcategory_id, promo_id, friendlyurl, directProducts }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [baseProducts, setBaseProducts] = useState([]);
  const [products, setProducts] = useState([]);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const nextUrlRef = useRef(null);
  const isFetchingMoreRef = useRef(false);
  useEffect(() => {
    if (directProducts && directProducts.length > 0) {
      const normalized = normalizeProducts(directProducts);
      setBaseProducts(normalized);
      setProducts(sortProducts(normalized, sortBy));
      nextUrlRef.current = null;
      setLoading(false);
    }
  }, [directProducts, sortBy]);

  const buildProductsUrl = useCallback(() => {
    const storeId = localStorage.getItem("storeId") || "";

    // ✅ Skip if we have directProducts
    if (directProducts) {
      return null;
    }

    if (promo_id) {
      return `/catlog/promotional/${promo_id}/items/?subcategory=${subcategory_id}&store=${storeId}`;
    }

    return `/catlog/products/?subcategory=${subcategory_id}&page_size=20&store=${storeId}&is_suspended=false`;
  }, [promo_id, subcategory_id, directProducts]);

  const fetchPage = useCallback(async (url) => {
    const response = await axiosConfig.get(normalizeNextUrl(url));
    return {
      items: normalizeProducts(response.data?.results || []),
      nextUrl: response.data?.next || null,
    };
  }, []);

  const loadProducts = useCallback(async () => {
    // ✅ Skip API if we have directProducts
    if (directProducts) {
      return;
    }

    if (!subcategory_id && !promo_id) {
      setBaseProducts([]);
      nextUrlRef.current = null;
      return;
    }

    setLoading(true);
    nextUrlRef.current = null;

    try {
      const url = buildProductsUrl();
      if (!url) return;

      const { items, nextUrl } = await fetchPage(url);
      setBaseProducts(items);
      nextUrlRef.current = nextUrl;
    } catch (error) {
      console.error("Error fetching products:", error);
      setBaseProducts([]);
    } finally {
      setLoading(false);
    }
  }, [buildProductsUrl, fetchPage, subcategory_id, promo_id, directProducts]);

  const loadMore = useCallback(async () => {
    // ✅ No pagination for direct products
    if (directProducts) return;

    if (!nextUrlRef.current || isFetchingMoreRef.current) {
      return;
    }

    isFetchingMoreRef.current = true;
    setLoadingMore(true);

    try {
      const { items, nextUrl } = await fetchPage(nextUrlRef.current);
      setBaseProducts((currentItems) =>
        normalizeProducts([...currentItems, ...items])
      );
      nextUrlRef.current = nextUrl;
    } catch (error) {
      console.error("Error loading more products:", error);
    } finally {
      isFetchingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [fetchPage, directProducts]);

  // ✅ Initial load - only if no directProducts
  useEffect(() => {
    setSortBy(DEFAULT_SORT);
    if (!directProducts) {
      loadProducts();
    }
  }, [loadProducts, directProducts]);

  // ✅ Update products when sorting changes
  useEffect(() => {
    if (baseProducts.length > 0 || (directProducts && directProducts.length > 0)) {
      setProducts(sortProducts(baseProducts, sortBy));
    }
  }, [baseProducts, sortBy, directProducts]);

  // ✅ Infinite scroll
  useEffect(() => {
    const handleScroll = () => {
      if (directProducts) return; // No infinite scroll for direct products
      if (!nextUrlRef.current || isFetchingMoreRef.current) {
        return;
      }

      const scrollPosition =
        window.innerHeight + document.documentElement.scrollTop;
      const threshold = document.documentElement.scrollHeight - 280;

      if (scrollPosition >= threshold) {
        loadMore();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [loadMore, directProducts]);
  const loadAllRemainingProducts = async () => {
    let allProducts = [...baseProducts];
    let nextUrl = nextUrlRef.current;

    while (nextUrl) {
      const response = await axiosConfig.get(normalizeNextUrl(nextUrl));

      allProducts = [
        ...allProducts,
        ...(response.data?.results || []),
      ];

      nextUrl = response.data?.next;
    }

    setBaseProducts(normalizeProducts(allProducts));
    nextUrlRef.current = null;
  };
  const handleSortChange = async (e) => {
    const value = e.target.value;

    if (
      value !== "Relevance" &&
      nextUrlRef.current
    ) {
      setLoading(true);
      await loadAllRemainingProducts();
      setLoading(false);
    }

    setSortBy(value);
  };
  return (
    <div className="products-container">
      {loading ? (
        <div className="loader-container">
          <img src={require("../images/loader.gif")} alt="Loading..." />
        </div>
      ) : (
        <>
          <div className="inner-container">
            <div className="top-info-container">
              <div className="d-flex justify-content-between">
                <div>
                  <p className="subcategory-title">{friendlyurl}</p>
                </div>
                <div className="d-flex flex-column">
                  <label htmlFor="sortby" className="form-label">
                    Sort By
                  </label>
                  {/* <select
                    className="Selectbar"
                    id="sortby"
                    name="sortby"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                  > */}
                  <select
                    className="Selectbar"
                    id="sortby"
                    name="sortby"
                    value={sortBy}
                    onChange={handleSortChange}
                  >
                    <option>Relevance</option>
                    <option>Price (Low to High)</option>
                    <option>Price (High to Low)</option>
                    <option>Discount (High to Low)</option>
                    <option>Name (A to Z)</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          {products.length > 0 ? (
            <div className="catagery-items-grid">
              {products.map((itemObj, index) => (
                <div className="catagery-item-product" key={itemObj.id || index}>
                  <Product item={itemObj} index={index} />
                </div>
              ))}

              {loadingMore && (
                <div className="loader-container">
                  <img src={require("../images/loader.gif")} alt="Loading..." />
                </div>
              )}
            </div>
          ) : (
            <div className="catagery-items-grid">
              <p className="subcategory-title">No products available.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export { ProductsList };
